import React, { useState, useEffect, useRef, useCallback } from 'react';
import './Questionnaire.css';
import questionnaireConfig from '../config/questionnaire-config.json';

// True on phones/tablets. Uses the user agent string, which is reliable for
// "is this a mobile device" — media queries like (pointer: coarse) misfire on
// touchscreen Windows laptops, which we want treated as desktop.
const IS_TOUCH_PRIMARY = typeof navigator !== 'undefined' &&
  /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini|Mobile/i
    .test(navigator.userAgent);

const validateEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);
const validatePhone = (v) => v.replace(/\D/g, '').length >= 8;
// To beslægtede formater, med vilje holdt adskilt:
//   'cpr_dk' → almindeligt dansk CPR, kun cifre
//   'cpr'    → e-CPR, hvor de sidste fire tegn kan være bogstaver
//
// Adskillelsen er ikke kosmetisk: kunder uden dansk CPR skal gennem e-CPR-grenen,
// fordi det er den eneste vej hvor vi også spørger om hospital. Tog CPR-feltet
// imod bogstaver, ville et e-CPR blive gemt som et almindeligt CPR, og sagen
// ville mangle et hospital.
const validateCPRDK = (v) => /^\d{6}-?\d{4}$/.test(String(v).replace(/\s/g, ''));
const validateCPR = (v) => /^\d{6}-?[0-9A-Z]{4}$/i.test(String(v).replace(/\s/g, ''));

// ---------------------------------------------------------------------------
// Fødselsdato
//
// Vi spørger med tre adskilte felter og en månedsliste med navne i stedet for
// en <input type="date">. Den native datovælger viser sit format efter
// browserens sprog, ikke sidens: en kunde med engelsk telefon får mm/dd/yyyy op
// midt i den danske formular. Taster de 10/01 og mener 10. januar, gemmes
// 1. oktober — begge er gyldige datoer, så intet led nedenfor kan opdage det.
// Det skete for ordre 2021, hvor vores dato og hospitalets e-CPR-nummer endte
// med at pege på hver sin dag.
//
// Med en månedsliste findes tvetydigheden ikke. Værdien ud af feltet er
// fortsat ISO (ÅÅÅÅ-MM-DD), så backend og DATE-kolonnen er uberørte.
// ---------------------------------------------------------------------------
const MIN_AAR = 1900;

const splitISO = (iso) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(iso || ''));
  return m ? { d: m[3], m: m[2], y: m[1] } : { d: '', m: '', y: '' };
};

// Returnerer { iso } når de tre dele udgør en rigtig dato, ellers { grund }.
// 'ufuldstaendig' er ikke en fejl at vise — kunden er bare ikke færdig endnu.
const composeISO = ({ d, m, y }) => {
  if (!d || !m || y.length !== 4) return { grund: 'ufuldstaendig' };
  const dag = Number(d), maaned = Number(m), aar = Number(y);
  const dato = new Date(Date.UTC(aar, maaned - 1, dag));
  // Fanger 31. februar: Date ruller over til 3. marts, så delene passer ikke.
  if (dato.getUTCDate() !== dag || dato.getUTCMonth() !== maaned - 1 || dato.getUTCFullYear() !== aar) {
    return { grund: 'findes_ikke' };
  }
  if (aar < MIN_AAR) return { grund: 'findes_ikke' };
  if (dato.getTime() > Date.now()) return { grund: 'fremtid' };
  return { iso: `${y}-${String(maaned).padStart(2, '0')}-${String(dag).padStart(2, '0')}` };
};

const formatCPR = (value, allowLetters) => {
  const allowed = allowLetters ? /[^0-9A-Z]/g : /[^0-9]/g;
  const cleaned = String(value).toUpperCase().replace(allowed, '').slice(0, 10);
  if (cleaned.length > 6) return cleaned.slice(0, 6) + '-' + cleaned.slice(6);
  return cleaned;
};

// Format phone as "+CC XX XX XX XX" or "XX XX XX XX" depending on whether
// the user typed a leading +. Country code is assumed to be the first 2
// digits after +. Good enough for Danish numbers (which is what we get).
// Render an answer as a human-readable string for the review screen.
const formatAnswer = (q, value, lang = 'da') => {
  const optLabel = (opt) =>
    (lang !== 'da' && opt.en?.label != null ? opt.en.label : opt.label);
  if (value === undefined || value === null) return null;
  if (Array.isArray(value)) {
    if (value.length === 0) return null;
    const labels = value.map(v => {
      const opt = (q.options || []).find(o => o.value === v);
      return opt ? optLabel(opt) : v;
    });
    return labels.join(', ');
  }
  if (typeof value === 'string' && !value.trim()) return null;
  if (q.type === 'radio') {
    const opt = (q.options || []).find(o => o.value === value);
    return opt ? optLabel(opt) : value;
  }
  if (q.type === 'number' && q.suffix) return `${value} ${q.suffix}`;
  // En dato skal skrives ud med månedens navn. Stod der "1986-10-01", skulle
  // kunden selv regne ud hvilken af de to cifre der var måneden — og det er
  // netop den forveksling gennemgangen skal fange.
  if (q.type === 'date') {
    const { d, m, y } = splitISO(value);
    if (!d) return String(value);
    const navn = UI[lang].months[Number(m) - 1];
    return lang === 'da' ? `${Number(d)}. ${navn} ${y}` : `${Number(d)} ${navn} ${y}`;
  }
  return String(value);
};

const formatPhone = (value) => {
  if (!value) return '';
  const trimmed = String(value).trim();
  const hasPlus = trimmed.startsWith('+');
  const digits = trimmed.replace(/\D/g, '');
  if (!digits) return hasPlus ? '+' : '';
  if (hasPlus) {
    const cc = digits.slice(0, 2);
    const rest = digits.slice(2);
    const groups = rest.match(/.{1,2}/g) || [];
    return '+' + cc + (groups.length ? ' ' + groups.join(' ') : '');
  }
  const groups = digits.match(/.{1,2}/g) || [];
  return groups.join(' ');
};

// Evaluate a question's `conditional` against the current value of the field
// it depends on. Supports four forms:
//   { field, value: "x" }            → show when field === "x"
//   { field, value: ["x", "y"] }     → show when field is one of the values
//   { field, notValue: "x" }         → show when field !== "x"
//   { field, notValue: ["x", "y"] }  → show when field is none of the values
// No conditional → always show.
const conditionMet = (conditional, actual) => {
  if (!conditional) return true;
  if (Array.isArray(conditional.value)) return conditional.value.includes(actual);
  if (Array.isArray(conditional.notValue)) return !conditional.notValue.includes(actual);
  if (conditional.notValue !== undefined) return actual !== conditional.notValue;
  return actual === conditional.value;
};

// ---------------------------------------------------------------------------
// Sprog. Dansk er standard; engelsk vælges via stien /en, så linket kan deles
// fra den engelske hjemmeside. ?lang= virker også, da bestillingslinket i
// forvejen bærer query-parametre og dermed er nemt at udvide fra Shopify.
// Valget huskes, så et sprogskifte ikke går tabt hvis siden genindlæses.
// ---------------------------------------------------------------------------
const LANGS = ['da', 'en'];
const LANG_KEY = 'celto_lang';

const detectLang = () => {
  if (typeof window === 'undefined') return 'da';
  const fromPath = /^\/en(\/|$)/i.test(window.location.pathname) ? 'en' : null;
  const fromQuery = new URLSearchParams(window.location.search).get('lang');
  let stored = null;
  try { stored = window.localStorage.getItem(LANG_KEY); } catch (e) { /* privat tilstand */ }
  const candidate = fromPath || (LANGS.includes(fromQuery) ? fromQuery : null) || stored;
  return LANGS.includes(candidate) ? candidate : 'da';
};

// UI-tekster der ikke hører til et bestemt spørgsmål.
const UI = {
  da: {
    langName: 'Dansk',
    review: 'Gennemgå',
    reviewTitle: 'Se dine svar igennem',
    reviewLead: 'Tjek at alt er korrekt før du sender. Klik på et felt for at rette det.',
    reviewEdit: 'Rediger',
    reviewEmpty: '(ikke besvaret)',
    requiredNote: '= påkrævet',
    skip: 'Spring over',
    optionalTag: 'Frivilligt',
    optionalTagSub: 'bruges til at forbedre vores service',
    whyAsk: 'Hvorfor spørger vi om det?',
    orderBadge: 'Ordre #',
    loading: 'Indlæser spørgeskema...',
    enterHint: 'eller tryk',
    enterKey: 'Enter ↵',
    textareaHint1: 'for at gå videre',
    textareaHint2: 'for ny linje',
    shiftEnter: 'Shift + Enter',
    navLabel: 'Naviger mellem spørgsmål',
    prev: 'Forrige spørgsmål',
    next: 'Næste spørgsmål',
    back: 'Tilbage',
    send: 'Send',
    branchHasEcpr: 'Jeg har ikke et almindeligt CPR-nummer, men har fået oprettet et e-CPR (erstatningspersonnummer)',
    branchNoCpr: 'Jeg har hverken CPR- eller e-CPR-nummer',
    branchReset: 'Jeg har alligevel et almindeligt CPR-nummer',
    kbToLetters: 'Mit nummer indeholder bogstaver',
    kbToDigits: 'Skift tilbage til taltastatur',
    iframeReceived: 'Dit spørgeskema er modtaget.',
    iframeRedirect: 'Du sendes nu automatisk til checkout...',
    errRequired: 'er påkrævet',
    errCpr: 'Ugyldigt CPR-nummer',
    errEcpr: 'Ugyldigt e-CPR-nummer',
    errDate: 'Den dato findes ikke',
    errDateFuture: 'Fødselsdatoen kan ikke ligge i fremtiden',
    dateDay: 'Dag',
    dateMonth: 'Måned',
    dateYear: 'År',
    dateMonthPlaceholder: 'Vælg måned',
    months: ['januar', 'februar', 'marts', 'april', 'maj', 'juni',
             'juli', 'august', 'september', 'oktober', 'november', 'december'],
    errEmail: 'Ugyldig email',
    errPhone: 'Ugyldigt telefonnummer',
    errMinLength: (n) => `Skal være mindst ${n} tegn`,
    errMaxLength: (n) => `Må højst være ${n} tegn`,
    errMin: (n) => `Skal være mindst ${n}`,
    errMissingLink: (label) => `Manglende oplysning fra dit bestillingslink (${label}). Brug venligst linket fra din ordrebekræftelse.`,
    errNetwork: 'Kunne ikke forbinde til serveren. Tjek din internetforbindelse og prøv igen.',
    errGeneric: 'Der opstod en fejl ved indsendelse. Prøv venligst igen eller kontakt support.',
  },
  en: {
    langName: 'English',
    review: 'Review',
    reviewTitle: 'Review your answers',
    reviewLead: 'Check that everything is correct before you submit. Click a field to change it.',
    reviewEdit: 'Edit',
    reviewEmpty: '(not answered)',
    requiredNote: '= required',
    skip: 'Skip',
    optionalTag: 'Optional',
    optionalTagSub: 'used to improve our service',
    whyAsk: 'Why do we ask?',
    orderBadge: 'Order #',
    loading: 'Loading questionnaire...',
    enterHint: 'or press',
    enterKey: 'Enter ↵',
    textareaHint1: 'to continue',
    textareaHint2: 'for a new line',
    shiftEnter: 'Shift + Enter',
    navLabel: 'Navigate between questions',
    prev: 'Previous question',
    next: 'Next question',
    back: 'Back',
    send: 'Submit',
    branchHasEcpr: 'I do not have an ordinary CPR number, but an e-CPR (replacement personal number) has been created for me',
    branchNoCpr: 'I have neither a CPR nor an e-CPR number',
    branchReset: 'I do have an ordinary CPR number after all',
    kbToLetters: 'My number contains letters',
    kbToDigits: 'Switch back to the number keypad',
    iframeReceived: 'Your questionnaire has been received.',
    iframeRedirect: 'You are being redirected to checkout...',
    errRequired: 'is required',
    errCpr: 'Invalid CPR number',
    errEcpr: 'Invalid e-CPR number',
    errDate: 'That date does not exist',
    errDateFuture: 'The date of birth cannot be in the future',
    dateDay: 'Day',
    dateMonth: 'Month',
    dateYear: 'Year',
    dateMonthPlaceholder: 'Select month',
    months: ['January', 'February', 'March', 'April', 'May', 'June',
             'July', 'August', 'September', 'October', 'November', 'December'],
    errEmail: 'Invalid email',
    errPhone: 'Invalid phone number',
    errMinLength: (n) => `Must be at least ${n} characters`,
    errMaxLength: (n) => `Must be at most ${n} characters`,
    errMin: (n) => `Must be at least ${n}`,
    errMissingLink: (label) => `Missing information from your order link (${label}). Please use the link from your order confirmation.`,
    errNetwork: 'Could not reach the server. Check your internet connection and try again.',
    errGeneric: 'Something went wrong while submitting. Please try again or contact support.',
  },
};

const Questionnaire = () => {
  const config = questionnaireConfig;

  const [lang, setLang] = useState(detectLang);
  const t = UI[lang];

  // Henter en tekst fra et spørgsmål eller en svarmulighed på det valgte sprog
  // og falder tilbage til dansk, hvis en oversættelse mangler. Så bliver en
  // glemt oversættelse en skønhedsfejl frem for et tomt felt.
  const tx = useCallback(
    (obj, key) => (lang !== 'da' && obj?.en?.[key] != null ? obj.en[key] : obj?.[key]),
    [lang]
  );

  const meta = lang !== 'da' && config.questionnaire.en
    ? { ...config.questionnaire, ...config.questionnaire.en }
    : config.questionnaire;

  // Holder <html lang> i sync, så skærmlæsere og browserens oversættelse
  // behandler siden som det sprog den faktisk vises på.
  useEffect(() => {
    document.documentElement.lang = lang;
    try { window.localStorage.setItem(LANG_KEY, lang); } catch (e) { /* privat tilstand */ }
  }, [lang]);

  // Skifter sprog og holder URL'en i sync, så den kan deles og genindlæses.
  // Query-parametrene bevares — de bærer ordreoplysningerne.
  const switchLang = (next) => {
    setLang(next);
    const { pathname, search, hash } = window.location;
    const base = pathname.replace(/^\/en(?=\/|$)/i, '') || '/';
    const path = next === 'en' ? '/en' + (base === '/' ? '' : base) : base;
    window.history.replaceState(null, '', path + search + hash);
  };

  const [formData, setFormData] = useState({});
  const [currentStep, setCurrentStep] = useState(0);
  const [direction, setDirection] = useState('forward');
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isCompleted, setIsCompleted] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [stepKey, setStepKey] = useState(0);
  // Dag, måned og år hver for sig, pr. spørgsmål. De kan ikke udledes af
  // formData, for en halvt udfyldt dato har ingen ISO-form — og uden dem ville
  // "1" i dagfeltet forsvinde igen, så snart man rørte måneden.
  const [dateParts, setDateParts] = useState({});
  // When the user clicks "Rediger" on the review screen, we record it so that
  // pressing "Næste" (or auto-advancing a radio) returns directly to review
  // instead of stepping through every remaining question.
  const [returnToReview, setReturnToReview] = useState(false);
  const [infoOpen, setInfoOpen] = useState(false);
  // CPR-feltet viser taltastatur som standard, da langt de fleste har et
  // almindeligt CPR-nummer. De få med e-CPR (bogstaver i de sidste fire tegn)
  // kan skifte til det almindelige tastatur.
  const [cprAlphaMode, setCprAlphaMode] = useState(false);

  const inputRef = useRef(null);

  // Initialize form data + read URL params for pre-fill
  useEffect(() => {
    const initialData = {};
    config.questions.forEach(q => {
      initialData[q.id] = q.type === 'checkbox' ? [] : '';
    });

    const params = new URLSearchParams(window.location.search);
    ['order_id', 'customer_id', 'customer_name', 'customer_email', 'order_total', 'order_details'].forEach(key => {
      const v = params.get(key);
      if (v) initialData[key] = v;
    });
    // Phone may arrive under a few different keys depending on Shopify template
    const phoneRaw = params.get('customer_phone') || params.get('phone') || params.get('telefon');
    if (phoneRaw) initialData.telefonnummer = formatPhone(phoneRaw);

    setFormData(initialData);
  }, [config]);

  // Apply config-driven colors
  useEffect(() => {
    const root = document.documentElement;
    const s = config.styling;
    if (!s) return;
    const fmt = (c) => (c && /^[0-9A-Fa-f]{6,8}$/.test(c) ? `#${c}` : c);
    root.style.setProperty('--primary-color', fmt(s.primaryColor));
    root.style.setProperty('--secondary-color', fmt(s.secondaryColor));
    root.style.setProperty('--error-color', fmt(s.errorColor));
    root.style.setProperty('--success-color', fmt(s.successColor));
  }, [config.styling]);

  // Decide which questions become visible steps
  const shouldShowAsStep = useCallback((q) => {
    if (q.type === 'hidden') return false;
    if (q.readonly) return false;
    if (!q.conditional) return true;
    return conditionMet(q.conditional, formData[q.conditional.field]);
  }, [formData]);

  const visibleQuestions = config.questions.filter(shouldShowAsStep);
  const totalSteps = visibleQuestions.length;
  // safeStep can equal totalSteps, which represents the review screen
  const safeStep = totalSteps === 0 ? 0 : Math.min(Math.max(0, currentStep), totalSteps);
  const isReview = totalSteps > 0 && safeStep === totalSteps;
  const currentQuestion = isReview ? null : visibleQuestions[safeStep];
  const isFirstStep = safeStep === 0;

  // Auto-focus the input when the step changes — synchronously after layout
  // so iOS Safari sees the new input gain focus immediately and is less likely
  // to dismiss the soft keyboard between steps.
  useEffect(() => {
    if (inputRef.current && typeof inputRef.current.focus === 'function') {
      inputRef.current.focus({ preventScroll: false });
    }
  }, [stepKey]);

  // Close the "why we ask" popover whenever the step changes.
  useEffect(() => {
    setInfoOpen(false);
  }, [stepKey]);

  // Per-field validation
  const validateField = (q, value) => {
    if (q.required) {
      if (q.type === 'checkbox' && (!Array.isArray(value) || value.length === 0)) {
        return tx(q, 'errorMessage') || `${tx(q, 'label')} ${t.errRequired}`;
      }
      if (q.type !== 'checkbox' && (value === undefined || value === null || (typeof value === 'string' && !value.trim()))) {
        return tx(q, 'errorMessage') || `${tx(q, 'label')} ${t.errRequired}`;
      }
    }
    if (!value || (typeof value === 'string' && !value.trim())) return null;

    const v = q.validation;
    if (!v) return null;

    const em = tx(q, 'errorMessage');
    if (v.format === 'cpr_dk' && !validateCPRDK(value)) return em || t.errCpr;
    if (v.format === 'cpr' && !validateCPR(value)) return em || t.errEcpr;
    if (v.format === 'email' && !validateEmail(value)) return em || t.errEmail;
    if (v.format === 'phone' && !validatePhone(value)) return em || t.errPhone;
    if (v.minLength && value.length < v.minLength) return em || t.errMinLength(v.minLength);
    if (v.maxLength && value.length > v.maxLength) return em || t.errMaxLength(v.maxLength);
    if (q.type === 'number' && v.min != null && (isNaN(value) || Number(value) < v.min)) {
      return em || t.errMin(v.min);
    }
    return null;
  };

  const handleInputChange = (id, value) => {
    // Build the next state, then clear any conditional question whose condition
    // is no longer met. Config order has parents before children, so a single
    // pass cascades correctly (e.g. changing detaljegrad clears næste_skridt,
    // which in turn hides fokus/styring/tempo).
    const next = { ...formData, [id]: value };
    config.questions.forEach(q => {
      if (q.conditional && !conditionMet(q.conditional, next[q.conditional.field])) {
        next[q.id] = q.type === 'checkbox' ? [] : '';
      }
    });
    setFormData(next);

    setErrors(prev => {
      const e = { ...prev, [id]: '' };
      config.questions.forEach(q => {
        if (q.conditional && !conditionMet(q.conditional, next[q.conditional.field])) {
          e[q.id] = '';
        }
      });
      return e;
    });
  };

  const submitForm = async () => {
    const newErrors = {};
    let firstHiddenError = null;
    config.questions.forEach(q => {
      if (q.conditional && !conditionMet(q.conditional, formData[q.conditional.field])) return;
      const err = validateField(q, formData[q.id]);
      if (err) {
        newErrors[q.id] = err;
        if (!firstHiddenError && (q.type === 'hidden' || q.readonly)) {
          firstHiddenError = q;
        }
      }
    });

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      if (firstHiddenError) {
        setSubmitError(t.errMissingLink(tx(firstHiddenError, 'label') || firstHiddenError.id));
        return;
      }
      const idx = visibleQuestions.findIndex(q => newErrors[q.id]);
      if (idx >= 0) {
        setCurrentStep(idx);
        setStepKey(k => k + 1);
      }
      return;
    }

    setIsSubmitting(true);
    setSubmitError('');

    try {
      const submitData = {
        ...formData,
        timestamp: new Date().toISOString(),
        submittedAt: new Date().toISOString(),
        userAgent: navigator.userAgent,
        ipAddress: null,
      };

      const response = await fetch('https://api.forms.celto.dk/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        mode: 'cors',
        body: JSON.stringify(submitData),
      });

      const result = await response.json().catch(() => ({ success: !response.ok ? false : true }));

      if (!response.ok || result.success === false) {
        throw new Error(result.error || 'HTTP ' + response.status);
      }

      if (window.parent !== window) {
        window.parent.postMessage({ type: 'questionnaire_completed', data: result }, '*');
      }

      setIsCompleted(true);
    } catch (error) {
      console.error('Error submitting questionnaire:', error);
      if (error.name === 'TypeError' && /fetch/i.test(error.message)) {
        setSubmitError(t.errNetwork);
      } else {
        setSubmitError(error.message || t.errGeneric);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const goNext = useCallback(() => {
    // On the review screen, "next" means submit
    if (isReview) {
      submitForm();
      return;
    }
    const q = visibleQuestions[safeStep];
    if (!q) return;
    const err = validateField(q, formData[q.id]);
    if (err) {
      setErrors(prev => ({ ...prev, [q.id]: err }));
      return;
    }
    setDirection('forward');
    if (returnToReview) {
      setReturnToReview(false);
      setCurrentStep(totalSteps); // jump straight back to review
    } else {
      setCurrentStep(safeStep + 1);
    }
    setStepKey(k => k + 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [safeStep, totalSteps, formData, visibleQuestions, isReview, returnToReview]);

  const goBack = useCallback(() => {
    if (safeStep === 0) return;
    setReturnToReview(false); // cancel return-to-review if user navigates back
    setDirection('back');
    setCurrentStep(safeStep - 1);
    setStepKey(k => k + 1);
  }, [safeStep]);

  // Jump to a specific question (used by the "rediger" buttons on review)
  const goToStep = (idx) => {
    setReturnToReview(true);
    setDirection('back');
    setCurrentStep(idx);
    setStepKey(k => k + 1);
    setSubmitError('');
  };

  // After picking a radio option, advance to the next step (or review).
  // If the user is editing from the review screen, jump straight back there.
  // Skifter mellem CPR-grenene. Trin-indekset bevares med vilje: cpr_status
  // ændrer hvilke spørgsmål der er synlige, så samme indeks peger nu på det
  // første spørgsmål i den nye gren. handleInputChange rydder selv felterne fra
  // den gren man forlader, så man ikke sender data fra to grene ind.
  const setCprStatus = (status) => {
    handleInputChange('cpr_status', status);
    setReturnToReview(false);
    setDirection(status ? 'forward' : 'back');
    setStepKey((k) => k + 1);
    setSubmitError('');
  };

  const advanceAfterRadio = () => {
    setDirection('forward');
    if (returnToReview) {
      setReturnToReview(false);
      setCurrentStep(totalSteps);
    } else {
      setCurrentStep(safeStep + 1);
    }
    setStepKey(k => k + 1);
  };

  // Enter advances on desktop. In a textarea on a touch device, Enter keeps
  // its native newline behaviour so mobile users can break lines — they use
  // the Næste button to advance. On desktop, Shift+Enter is the newline.
  const handleKeyDown = (e) => {
    if (e.key !== 'Enter' || e.shiftKey) return;
    if (currentQuestion?.type === 'textarea' && IS_TOUCH_PRIMARY) return;
    e.preventDefault();
    goNext();
  };

  // Global Up/Down arrows navigate between questions, mirroring the on-screen
  // arrow buttons. Skipped when focus is in a textarea so the cursor can move.
  useEffect(() => {
    const handler = (e) => {
      if (e.key !== 'ArrowUp' && e.key !== 'ArrowDown') return;
      if (e.shiftKey || e.ctrlKey || e.metaKey || e.altKey) return;
      const tag = (e.target?.tagName || '').toLowerCase();
      // I en textarea flytter pilene markøren, og i månedslisten vælger de
      // måned. Begge steder ville et spring til næste spørgsmål være forkert.
      if (tag === 'textarea' || tag === 'select') return;
      e.preventDefault();
      if (e.key === 'ArrowUp') goBack();
      else goNext();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [goBack, goNext]);

  // Letter shortcuts (A, B, C...) for radio questions
  useEffect(() => {
    if (!currentQuestion || currentQuestion.type !== 'radio') return;
    const handler = (e) => {
      const tag = (e.target.tagName || '').toLowerCase();
      if (tag === 'input' || tag === 'textarea') return;
      if (e.key.length !== 1) return;
      const code = e.key.toUpperCase().charCodeAt(0);
      const idx = code - 65;
      const opts = currentQuestion.options || [];
      if (idx >= 0 && idx < opts.length) {
        e.preventDefault();
        const opt = opts[idx];
        handleInputChange(currentQuestion.id, opt.value);
        setTimeout(advanceAfterRadio, 160);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentQuestion, safeStep, totalSteps]);

  const renderInput = (q) => {
    const value = formData[q.id] ?? '';
    const hasError = !!errors[q.id];
    const inputClass = `tf-input ${hasError ? 'tf-input--error' : ''}`;

    switch (q.type) {
      case 'text': {
        // Begge formater formateres med bindestreg, men kun e-CPR ('cpr')
        // tillader bogstaver — og kun der er tastaturskifteren relevant.
        const fmt = q.validation?.format;
        const isCprLike = fmt === 'cpr' || fmt === 'cpr_dk';
        const allowLetters = fmt === 'cpr';
        const field = (
          <input
            ref={inputRef}
            id={q.id}
            type="text"
            value={value}
            placeholder={tx(q, 'placeholder')}
            className={inputClass}
            onChange={(e) => {
              let v = e.target.value;
              if (isCprLike) {
                v = formatCPR(v, allowLetters);
                // Indsætter man et e-CPR med bogstaver, slås bogstavtilstand
                // til af sig selv — ellers kunne man ikke rette i det bagefter.
                if (allowLetters && /[A-Z]/.test(v)) setCprAlphaMode(true);
              }
              handleInputChange(q.id, v);
            }}
            onKeyDown={handleKeyDown}
            maxLength={q.validation?.maxLength}
            autoComplete="off"
            inputMode={isCprLike ? (allowLetters && cprAlphaMode ? 'text' : 'numeric') : undefined}
            autoCapitalize={allowLetters ? 'characters' : undefined}
            spellCheck={isCprLike ? false : undefined}
          />
        );

        // Skifteren vises kun på e-CPR-feltet, og kun på touch-enheder — på
        // desktop skriver man bogstaver på det fysiske tastatur uanset inputMode.
        if (!allowLetters || !IS_TOUCH_PRIMARY) return field;

        return (
          <>
            {field}
            <button
              type="button"
              className="tf-keyboard-toggle"
              onClick={() => {
                setCprAlphaMode((m) => !m);
                // iOS og Android læser først inputMode igen når feltet får
                // fokus på ny, så tastaturet skal tvinges til at skifte.
                const el = inputRef.current;
                if (el) {
                  el.blur();
                  setTimeout(() => el.focus(), 0);
                }
              }}
            >
              {cprAlphaMode ? t.kbToDigits : t.kbToLetters}
            </button>
          </>
        );
      }

      case 'date': {
        const parts = dateParts[q.id] || splitISO(value);
        const saetDel = (hvilken, raa) => {
          const rene = hvilken === 'm' ? raa : raa.replace(/\D/g, '');
          const naeste = { ...parts, [hvilken]: rene.slice(0, hvilken === 'y' ? 4 : 2) };
          setDateParts(p => ({ ...p, [q.id]: naeste }));
          const { iso, grund } = composeISO(naeste);
          // Er datoen ikke hel endnu, sendes tom værdi videre. Så er feltet
          // stadig "ubesvaret", og den påkrævet-kontrol der findes i forvejen
          // stopper kunden ved Næste.
          handleInputChange(q.id, iso || '');
          if (grund === 'findes_ikke' || grund === 'fremtid') {
            const besked = grund === 'fremtid' ? t.errDateFuture : t.errDate;
            setErrors(prev => ({ ...prev, [q.id]: besked }));
          }
        };
        return (
          <div className="tf-date-row">
            <label className="tf-date-del tf-date-del--dag">
              <span className="tf-date-label">{t.dateDay}</span>
              <input
                ref={inputRef}
                id={q.id}
                type="text"
                inputMode="numeric"
                autoComplete="bday-day"
                value={parts.d}
                placeholder="--"
                className={inputClass}
                onChange={(e) => saetDel('d', e.target.value)}
                onKeyDown={handleKeyDown}
              />
            </label>
            <label className="tf-date-del tf-date-del--maaned">
              <span className="tf-date-label">{t.dateMonth}</span>
              {/* Månedens navn, ikke dens tal. Det er her tvetydigheden
                  forsvinder: "januar" kan ikke læses som en dag. */}
              <select
                value={parts.m}
                autoComplete="bday-month"
                className={`${inputClass} tf-date-select`}
                onChange={(e) => saetDel('m', e.target.value)}
              >
                <option value="">{t.dateMonthPlaceholder}</option>
                {t.months.map((navn, i) => (
                  <option key={navn} value={String(i + 1).padStart(2, '0')}>{navn}</option>
                ))}
              </select>
            </label>
            <label className="tf-date-del tf-date-del--aar">
              <span className="tf-date-label">{t.dateYear}</span>
              <input
                type="text"
                inputMode="numeric"
                autoComplete="bday-year"
                value={parts.y}
                placeholder="----"
                className={inputClass}
                onChange={(e) => saetDel('y', e.target.value)}
                onKeyDown={handleKeyDown}
              />
            </label>
          </div>
        );
      }

      case 'email':
        return (
          <input
            ref={inputRef}
            id={q.id}
            type="email"
            value={value}
            placeholder={tx(q, 'placeholder')}
            className={inputClass}
            onChange={(e) => handleInputChange(q.id, e.target.value)}
            onKeyDown={handleKeyDown}
            inputMode="email"
            autoComplete="email"
          />
        );

      case 'tel':
        return (
          <input
            ref={inputRef}
            id={q.id}
            type="tel"
            value={value}
            placeholder={tx(q, 'placeholder')}
            className={inputClass}
            onChange={(e) => handleInputChange(q.id, formatPhone(e.target.value))}
            onKeyDown={handleKeyDown}
            inputMode="tel"
            autoComplete="tel"
          />
        );

      case 'number':
        return (
          <div className="tf-number-wrap">
            <input
              ref={inputRef}
              id={q.id}
              type="number"
              value={value}
              placeholder={tx(q, 'placeholder')}
              className={inputClass}
              onChange={(e) => handleInputChange(q.id, e.target.value)}
              onKeyDown={handleKeyDown}
              min={q.validation?.min}
              step={q.validation?.step}
              inputMode="decimal"
            />
            {q.suffix && <span className="tf-number-suffix">{q.suffix}</span>}
          </div>
        );

      case 'textarea':
        return (
          <textarea
            ref={inputRef}
            id={q.id}
            value={value}
            placeholder={tx(q, 'placeholder')}
            className={`tf-textarea ${hasError ? 'tf-input--error' : ''}`}
            onChange={(e) => handleInputChange(q.id, e.target.value)}
            onKeyDown={handleKeyDown}
            rows={q.rows || 4}
          />
        );

      case 'radio':
        return (
          <div className="tf-radio-group" role="radiogroup" aria-label={tx(q, 'label')}>
            {(q.options || []).map((opt, i) => {
              const letter = String.fromCharCode(65 + i);
              const selected = value === opt.value;
              return (
                <button
                  key={opt.value}
                  type="button"
                  role="radio"
                  aria-checked={selected}
                  ref={i === 0 ? inputRef : null}
                  className={`tf-radio-card ${selected ? 'tf-radio-card--selected' : ''}`}
                  onClick={() => {
                    // Clicking the already-selected option clears it (so you can
                    // remove your answer) instead of advancing. Any other option
                    // selects it and moves on.
                    if (selected) {
                      handleInputChange(q.id, '');
                      return;
                    }
                    handleInputChange(q.id, opt.value);
                    setTimeout(advanceAfterRadio, 160);
                  }}
                >
                  <span className="tf-radio-letter">{letter}</span>
                  <span className="tf-radio-text">
                    <span className="tf-radio-label">{tx(opt, 'label')}</span>
                    {tx(opt, 'description') && (
                      <span className="tf-radio-desc">{tx(opt, 'description')}</span>
                    )}
                  </span>
                  {selected && <span className="tf-radio-check" aria-hidden="true">✓</span>}
                </button>
              );
            })}
          </div>
        );

      case 'checkbox': {
        const selected = Array.isArray(value) ? value : [];
        return (
          <div className="tf-radio-group" role="group" aria-label={tx(q, 'label')}>
            {(q.options || []).map((opt, i) => {
              const letter = String.fromCharCode(65 + i);
              const isOn = selected.includes(opt.value);
              return (
                <button
                  key={opt.value}
                  type="button"
                  role="checkbox"
                  aria-checked={isOn}
                  ref={i === 0 ? inputRef : null}
                  className={`tf-radio-card ${isOn ? 'tf-radio-card--selected' : ''}`}
                  onClick={() => {
                    const nextVals = isOn
                      ? selected.filter(v => v !== opt.value)
                      : [...selected, opt.value];
                    handleInputChange(q.id, nextVals);
                  }}
                >
                  <span className="tf-radio-letter tf-radio-letter--check">{letter}</span>
                  <span className="tf-radio-label">{tx(opt, 'label')}</span>
                  {isOn && <span className="tf-radio-check" aria-hidden="true">✓</span>}
                </button>
              );
            })}
          </div>
        );
      }

      default:
        return null;
    }
  };

  if (isCompleted) {
    const isInIframe = window.parent !== window;
    return (
      <div className="tf-container">
        <div className="tf-completion">
          <h2 className="tf-completion-title">{meta.completionTitle}</h2>
          {isInIframe ? (
            <>
              <p>{t.iframeReceived}</p>
              <p><strong>{t.iframeRedirect}</strong></p>
              <div className="tf-spinner" />
            </>
          ) : (
            <p style={{ whiteSpace: 'pre-line' }}>{meta.completionMessage}</p>
          )}
        </div>
      </div>
    );
  }

  if (!isReview && !currentQuestion) {
    return (
      <div className="tf-container">
        <div className="tf-completion">
          <p>{t.loading}</p>
        </div>
      </div>
    );
  }

  const error = currentQuestion ? errors[currentQuestion.id] : null;
  const orderIdValue = formData.order_id;
  const totalWithReview = totalSteps + 1;
  const progress = totalSteps > 0 ? ((safeStep + 1) / totalWithReview) * 100 : 0;
  const submitLabel = isSubmitting ? meta.submitButtonLoadingText : meta.submitButtonText;
  const nextLabel = meta.nextButtonText;

  return (
    <div className="tf-container">
      <div className="tf-progress" aria-hidden="true">
        <div className="tf-progress-bar" style={{ width: `${progress}%` }} />
      </div>

      <div className="tf-topbar">
        {orderIdValue ? (
          <div className="tf-order-badge">{t.orderBadge}{orderIdValue}</div>
        ) : <span />}
        <img
          className="tf-wordmark"
          src={`${process.env.PUBLIC_URL || ''}/celto-wordmark.png`}
          alt="Celto"
        />
        <div className="tf-topbar-right">
          <div className="tf-lang" role="group" aria-label="Language">
            {LANGS.map((code) => (
              <button
                key={code}
                type="button"
                className={`tf-lang-btn${code === lang ? ' tf-lang-btn--active' : ''}`}
                aria-pressed={code === lang}
                onClick={() => switchLang(code)}
              >
                {code.toUpperCase()}
              </button>
            ))}
          </div>
          <div className="tf-step-counter">
            {isReview ? t.review : <>{safeStep + 1} <span>/</span> {totalSteps}</>}
          </div>
        </div>
      </div>

      {isReview ? (
        <div key={stepKey} className={`tf-step tf-step--${direction}`}>
          <div className="tf-step-content tf-review">
            <h2 className="tf-question">{t.reviewTitle}</h2>
            <p className="tf-description">{t.reviewLead}</p>

            <ul className="tf-review-list">
              {visibleQuestions.map((q, idx) => {
                const ans = formatAnswer(q, formData[q.id], lang);
                return (
                  <li key={q.id} className="tf-review-item">
                    <button
                      type="button"
                      className="tf-review-row"
                      onClick={() => goToStep(idx)}
                    >
                      <span className="tf-review-label">
                        {tx(q, 'label')}
                        {q.required ? (
                          <span className="tf-required" aria-hidden="true">*</span>
                        ) : (
                          <span className="tf-review-optional">{t.optionalTag}</span>
                        )}
                      </span>
                      <span className={`tf-review-value ${ans ? '' : 'tf-review-value--empty'}`}>
                        {ans || <em>{t.reviewEmpty}</em>}
                      </span>
                      <span className="tf-review-edit" aria-hidden="true">{t.reviewEdit}</span>
                    </button>
                  </li>
                );
              })}
            </ul>

            <p className="tf-review-legend">
              <span className="tf-required-note-star" aria-hidden="true">*</span> {t.requiredNote}
            </p>

            {submitError && <div className="tf-submit-error" role="alert">{submitError}</div>}

            <div className="tf-actions">
              <button
                ref={inputRef}
                type="button"
                className="tf-btn-primary"
                onClick={submitForm}
                disabled={isSubmitting}
              >
                {submitLabel}
              </button>
              {!IS_TOUCH_PRIMARY && (
                <span className="tf-hint">{t.enterHint} <strong>{t.enterKey}</strong></span>
              )}
            </div>
          </div>
        </div>
      ) : (
        <div key={stepKey} className={`tf-step tf-step--${direction}`}>
          <div className="tf-step-content">
            <div className="tf-question-number">
              <span className="tf-question-number-step">{safeStep + 1} <span aria-hidden="true">→</span></span>
              {tx(currentQuestion, 'whyAsk') && (
                <span className={`tf-info-wrap${infoOpen ? ' tf-info-open' : ''}`}>
                  <button
                    type="button"
                    className="tf-info-icon"
                    aria-label={t.whyAsk}
                    aria-expanded={infoOpen}
                    onClick={() => setInfoOpen(o => !o)}
                  >
                    i
                  </button>
                  <span className="tf-info-popover" role="tooltip">
                    <strong>{t.whyAsk}</strong>
                    <span>{tx(currentQuestion, 'whyAsk')}</span>
                  </span>
                </span>
              )}
            </div>

            <h2 className="tf-question">
              {tx(currentQuestion, 'label')}
              {currentQuestion.required && <span className="tf-required" aria-hidden="true">*</span>}
            </h2>

            {tx(currentQuestion, 'description') && (
              <p className="tf-description">{tx(currentQuestion, 'description')}</p>
            )}

            {tx(currentQuestion, 'infoBox') && (
              <div className="tf-info-box">
                {tx(currentQuestion, 'infoBox').map((paragraph, i) => (
                  <p key={i}>{paragraph}</p>
                ))}
              </div>
            )}

            <div className="tf-input-wrap">
              {renderInput(currentQuestion)}
            </div>

            {error && <div className="tf-error" role="alert">{error}</div>}

            {currentQuestion.type === 'radio' && !currentQuestion.required && (
              <div className="tf-actions">
                <button
                  type="button"
                  className="tf-btn-skip"
                  onClick={goNext}
                  disabled={isSubmitting}
                >
                  {t.skip}
                </button>
                <span className="tf-optional-tag">
                  <strong>{t.optionalTag}</strong>
                  <span className="tf-optional-tag-sub">{t.optionalTagSub}</span>
                </span>
              </div>
            )}

            {currentQuestion.type !== 'radio' && (
              <div className="tf-actions">
                <button
                  type="button"
                  className="tf-btn-primary"
                  onClick={goNext}
                  disabled={isSubmitting}
                >
                  {nextLabel}
                  {!isSubmitting && <span className="tf-btn-arrow" aria-hidden="true">↵</span>}
                </button>
                {currentQuestion.type !== 'textarea' && !IS_TOUCH_PRIMARY && (
                  <span className="tf-hint">{t.enterHint} <strong>{t.enterKey}</strong></span>
                )}
                {currentQuestion.type === 'textarea' && !IS_TOUCH_PRIMARY && (
                  <span className="tf-hint">
                    <strong>{t.enterKey}</strong> {t.textareaHint1} · <strong>{t.shiftEnter}</strong> {t.textareaHint2}
                  </span>
                )}
              </div>
            )}

            {/* Udgange fra CPR-spørgsmålet for kunder uden dansk CPR-nummer.
                At sætte cpr_status skjuler cpr-feltet og folder den relevante
                gren ud; da trinnet er et indeks i listen af synlige spørgsmål,
                lander brugeren automatisk på grenens første spørgsmål. */}
            {currentQuestion.id === 'cpr' && (
              <div className="tf-branch">
                <button type="button" className="tf-branch-btn"
                  onClick={() => setCprStatus('har_ecpr')}>
                  {t.branchHasEcpr}
                </button>
                <button type="button" className="tf-branch-btn"
                  onClick={() => setCprStatus('har_ikke')}>
                  {t.branchNoCpr}
                </button>
              </div>
            )}

            {(currentQuestion.id === 'ecpr_nummer' || currentQuestion.id === 'ecpr_id_type') && (
              <div className="tf-branch">
                <button type="button" className="tf-branch-btn"
                  onClick={() => setCprStatus('')}>
                  {t.branchReset}
                </button>
              </div>
            )}

            {submitError && <div className="tf-submit-error" role="alert">{submitError}</div>}

            {currentQuestion.required && (
              <p className="tf-required-note">
                <span className="tf-required-note-star" aria-hidden="true">*</span> {t.requiredNote}
              </p>
            )}
          </div>
        </div>
      )}

      <div className="tf-nav" aria-label={t.navLabel}>
        <button
          type="button"
          className="tf-nav-btn"
          onClick={goBack}
          disabled={isFirstStep}
          aria-label={t.prev}
          title={t.back}
        >
          <span aria-hidden="true">↑</span>
        </button>
        <button
          type="button"
          className="tf-nav-btn"
          onClick={goNext}
          disabled={isSubmitting}
          aria-label={isReview ? meta.submitButtonText : t.next}
          title={isReview ? t.send : nextLabel}
        >
          <span aria-hidden="true">↓</span>
        </button>
      </div>
    </div>
  );
};

export default Questionnaire;
